import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Check, Circle } from "lucide-react";
import FamilyPickerDialog from "@/components/activate/FamilyPickerDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { openExternalLink } from "@/lib/open-external-link";
import { useBrandBriefCount, useProfileCompleteness } from "@/hooks/use-brand-profile";
import { useActivationAccess } from "@/hooks/use-activation-access";
import { T } from "@/components/brand/profile-type";
import { ALL_FAMILIES, FAMILIES, type FamilyKey } from "@/data/activation-families";
import { joinOr } from "@/lib/join-or";
import { missingHint } from "@/lib/brand-profile";

const CAL_URL = "https://cal.com/marguerite-japy-u27fez/discovery-call";

// The same two looks as the landing page CTAs, a little smaller for a list, and a quiet outline for the least important step.
const SECONDARY = "h-10 gap-1.5 rounded-lg border border-[#5b4fff]/15 bg-transparent bg-gradient-to-r from-[#5b4fff]/10 to-[#8b7fff]/[0.06] px-5 text-[15px] font-medium text-[#5b4fff] shadow-none transition hover:scale-[1.02] hover:bg-transparent hover:from-[#5b4fff]/15 hover:to-[#8b7fff]/10 active:scale-100";
// The quietest of the three: white with a fine border, for a plain utility action.
const OUTLINE = "h-10 gap-1.5 rounded-lg border border-foreground/15 bg-white px-5 text-[15px] font-medium text-foreground shadow-none transition hover:scale-[1.02] hover:border-foreground/30 hover:bg-white active:scale-100";
const PRIMARY = "h-10 gap-1.5 rounded-lg border border-black/10 bg-[hsl(var(--ernest-neon))] px-5 text-[15px] font-medium text-foreground shadow-none transition hover:scale-[1.02] hover:bg-[hsl(var(--ernest-neon))] active:scale-100";

/** What a brand that is not approved yet can do now: finish its profile, book a call, send a first brief. */
const NextSteps = ({ onNavigate }: { onNavigate?: () => void }) => {
  const navigate = useNavigate();
  const { missing } = useProfileCompleteness();
  const { data: briefCount = 0 } = useBrandBriefCount();
  const { families } = useActivationAccess();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickable: FamilyKey[] = families.length ? families : ALL_FAMILIES;

  const go = (path: string) => { onNavigate?.(); navigate(path); };
  // Complete means complete: one missing item keeps the step open, which nudges brands to fill it in.
  const profileDone = missing.length === 0;
  const briefed = briefCount > 0;

  const steps = [
    {
      key: "brief",
      label: "Send your first brief",
      hint: "Say what you want to do. ernest finds the communities.",
      done: briefed,
      button: { label: "Start a brief", className: PRIMARY, run: () => setPickerOpen(true) },
    },
    {
      key: "call",
      label: "Book a call with the team",
      hint: "Not sure where to start? We will help.",
      done: false,
      button: { label: "Book a time", className: SECONDARY, icon: Calendar, run: () => openExternalLink(CAL_URL) },
    },
    {
      key: "profile",
      label: "Complete your profile",
      hint: missingHint(missing),
      done: profileDone,
      button: { label: "Edit profile", className: OUTLINE, run: () => go("/brand") },
    },
  ];

  return (
    <div>
      <ul className="divide-y divide-border/60 text-left">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-3.5 px-7 py-3">
            {s.done ? (
              <Check className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : (
              // The bubble does what the step's button does.
              <button type="button" onClick={s.button.run} aria-label={s.button.label} title={s.button.label}
                className="group/bubble -m-1 shrink-0 rounded-full p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Circle className="h-4 w-4 text-muted-foreground/50 transition-colors group-hover/bubble:text-foreground group-hover/bubble:fill-muted" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className={cn(T.key, "leading-snug", s.done && "text-muted-foreground line-through")}>{s.label}</div>
              <div className={cn(T.hint, "mt-0.5 truncate")}>{s.hint}</div>
            </div>
            {!s.done && (
              <Button onClick={s.button.run} className={cn("shrink-0", s.button.className)}>
                {s.button.label}
                {s.button.icon && <s.button.icon className="h-4 w-4" />}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <FamilyPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        families={pickable}
        onPick={(f) => go(`/activate/${f}`)}
        title="What do you want to do?"
        description="Pick one. Tell us what you need, and ernest will find the right communities."
      />
    </div>
  );
};

export default NextSteps;
